/* globals serverParams */
import { displayFn, guff } from 'bam-node-baseball-savant/utils/guff'

const {
    entity_code,
    is_team_type,
    splitYears,
    target_base,
    season_start,
    season_end,
} = serverParams

const isLeague = entity_code === 'League'
const showSprintSpeed =
    entity_code === 'Run' && (season_start === season_end || splitYears)

// Match player-update section if changing
const prevBaseMap = { '2B': '1B', '3B': '2B', All: 'Base' }

function lastNameFirstInitial(name) {
    const [last, first] = name.split(', ')
    if (!name || !first) {
        return '--'
    }

    return `${last}, ${first[0]}.`
}

const videoColumn = {
    name: '',
    field: '',
    headerClass: `th-component-header align-center th-vertical-bottom cursor-initial border-left ${
        isLeague ? 'hide' : ''
    }`,
    fieldClass: `tr-data border-left ${isLeague ? 'hide' : ''}`,
    sortable: false,
    displayFn: (val, row) => {
        if (!row.runner_moved) {
            return ''
        }

        return `
            <span data-video-icon style="inline-block; margin-left: 2px; padding: 2px; border-radius: 50%; border: 2px solid #c2c2c2;">
                <img src="/site-core/images/video.png" style="width: 18px; cursor: pointer;">
            </span>
            `
    },
}

const headers1 = [
    {
        name: is_team_type ? 'Team' : 'Player',
        field: 'entity_name',
        customSortField: 'entity_name',
        headerClass: `th-component-header align-left th-vertical-bottom table-static-column`,
        fieldClass: `tr-data align-left table-static-column`,
        photo: is_team_type
            ? undefined
            : '<img class="player-mug" onError="this.onerror=null;this.src=\'https://content.mlb.com/images/headshots/current/60x60/generic.png\';" src="https://content.mlb.com/images/headshots/current/60x60/{1}.png">',
        photoId: 'entity_id',
        defaultSortDir: 'asc',
        displayFn: (entity_name, d) =>
            is_team_type
                ? entity_name
                : `<a href="/savant-player/${d.entity_id}">${entity_name}</a>`,
    },
    {
        name: is_team_type ? '' : '',
        field: is_team_type ? 'entity_name' : 'team_name',
        headerClass: `th-component-header`,
        fieldClass: `tr-data`,
        photo: '<img class="table-team-logo" src="{1}" onerror="this.onerror=\'\';this.src=\'/site-core/images/savant-mlb.svg\'" />',
        photoId: 'team_image_url',
        defaultSortDir: 'asc',
        photoOnly: true,
    },
    {
        name: 'Year',
        field: 'start_year',
        headerClass: `th-component-header align-center th-vertical-bottom ${
            serverParams.splitYears ? '' : ' hide'
        }`,
        fieldClass: `tr-data ${serverParams.splitYears ? '' : ' hide'}`,
    },
    {
        name: 'Runner<br/>Stealing<br/>Runs',
        field: 'runs_stolen_on_running_act',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn: (val) => guff(val, 'runner_stealing_runs'),

        toolTip:
            'A run value metric taking into account that outs are more valuable than an extra base',
    },
    {
        name: 'Net Bases<br/>Gained',
        field: 'simple_stolen_on_running_act',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left border-top',
        fieldClass: 'tr-data align-center border-left',
        type: 'number',
        displayFn,
        toolTip:
            'Net Bases Gained: A sum of advances gained and outs created assigned to the runner',
        highlightDynamic: true,
    },
    {
        name: 'Bases<br/>Gained<br/>vs Avg',
        // name: 'Adv. <br/>Prev. <br/>vs. Avg',
        field: 'net_act_plus',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-top',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn,
        toolTip:
            'Bases Gained vs Avg: Advances (stolen bases and balks) gained compared to an average runner',
    },
    {
        name: 'Outs<br/>Created<br/>vs Avg',
        field: 'net_act_minus',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-right border-top',
        fieldClass: 'tr-data align-center border-right',
        type: 'number',
        displayFn,
        toolTip:
            'Outs (caught stealing and pickoffs) compared to an average runner',
    },
    {
        name: 'Season <br/>Sprint <br/> Speed',
        field: 'r_sprint_speed_top50percent',
        headerClass: `th-component-header align-center th-vertical-bottom ${
            !showSprintSpeed ? 'hide' : ''
        }`,
        fieldClass: `tr-data align-center ${!showSprintSpeed ? 'hide' : ''}`,
        type: 'number',
        displayFn: (val) => guff(val, 'sprint_speed'),
        toolTip:
            "Sprint speed on the runner's top 50 percent of qualified runs",
    },
    {
        name: 'SB <br/>Opp',
        field: 'n_init',
        headerClass: 'th-component-header align-center',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn: (val) => val.toLocaleString(),
        toolTip:
            'Stolen Base Opportunities: Pitches thrown with a runner on first only (no other runners)',
    },
    {
        name: 'SB <br/>Attempt<br/> Percent',
        field: 'rate_sbx',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn: (value) => guff(value, 'rate_sbx'),
        toolTip:
            'Stolen Base Attempt Percent: Stolen base attempts per opportunity',
    },
]

const headers2 = [
    {
        name: 'Bases<br/>Gained',
        field: 'n_plus',
        headerClass: 'th-component-header align-center',
        fieldClass: 'tr-data align-center',
        type: 'number',
    },
    {
        name: 'Outs<br/>Created',
        field: 'n_minus',
        headerClass: 'th-component-header align-center',
        fieldClass: 'tr-data align-center',
        type: 'number',
    },
    {
        name: 'Lead<br/>Dist.<br/>Gained',
        field: 'r_sec_minus_prim_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left',
        fieldClass: 'tr-data align-center border-left',
        type: 'number',
        displayFn: (value) => guff(value, 'r_sec_minus_prim_lead'),
        toolTip:
            'Lead Distance Gained: Distance a runner has advanced from the start of delivery to pitch release (in feet)',
    },
]

const headers3 = [
    {
        name: 'Lead<br/>Dist.<br/>Gained',
        field: 'r_sec_minus_prim_lead_sbx',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left',
        fieldClass: 'tr-data align-center border-left',
        type: 'number',
        displayFn: (value) => guff(value, 'r_sec_minus_prim_lead_sbx'),
        toolTip:
            'Lead Distance Gained: Distance in feet a runner has advanced from the start of delivery to pitch release (in feet)',
    },
]

const headersExpanded1 = [
    {
        name: 'SB',
        field: 'n_sb',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left expanded-column-highlight',
        fieldClass:
            'tr-data align-center border-left expanded-column-highlight',
        type: 'number',
        toolTip: 'Stolen Bases',
    },
    {
        name: 'CS',
        field: 'n_cs',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        toolTip: 'Caught Stealings',
    },
    {
        name: 'PK',
        field: 'n_pk',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        toolTip: 'Pickoffs',
    },
    {
        field: 'n_bk',
        name: 'BK',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        toolTip: 'Balks',
    },
    {
        name: 'FB',
        field: 'n_fb',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-right expanded-column-highlight',
        fieldClass:
            'tr-data align-center border-right expanded-column-highlight',
        type: 'number',
        toolTip:
            'Forced Balks: Balks caused by a pitcher failing to throw a runner out on a third disengagement',
    },
]

const headersExpanded2 = [
    {
        name: "At<br/> Pitcher's<br/>1st Move",
        field: 'r_primary_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        displayFn: (value) => guff(value, 'r_primary_lead'),
        toolTip:
            'Distance the runner is from first when the pitcher starts his delivery',
    },
    {
        name: 'At<br/> Pitch<br/>Rel',
        field: 'r_secondary_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        displayFn: (value) => guff(value, 'r_secondary_lead'),
        toolTip:
            'Distance the runner is from first at the pitch release (in feet)',
    },
]

const headersExpanded3 = [
    {
        name: "At<br/>Pitcher's<br/>1st Move",
        field: 'r_primary_lead_sbx',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        displayFn: (value) => guff(value, 'r_primary_lead_sbx'),
        toolTip:
            'Distance the runner is from first when the pitcher starts his delivery',
    },
    {
        name: 'At <br/> Pitch<br/>Rel',
        field: 'r_secondary_lead_sbx',
        headerClass:
            'th-component-header align-center th-vertical-bottom expanded-column-highlight',
        fieldClass: 'tr-data align-center expanded-column-highlight',
        type: 'number',
        displayFn: (value) => guff(value, 'r_secondary_lead_sbx'),
        toolTip:
            'Distance the runner is from first at the pitch release (in feet)',
    },
]

const headersNormal = [...headers1, ...headers2, ...headers3, videoColumn]

const headersExpanded = [
    ...headers1,
    ...headersExpanded1,
    ...headers2,
    ...headersExpanded2,
    ...headers3,
    ...headersExpanded3,
    videoColumn,
]

export const headers = {
    normal: headersNormal,
    expanded: headersExpanded,
}

// The -2 offset is needed to put the ft/s where we need for the sprint speed label while still accounting
// for the dynamic number of rows due to the sprint speed and split years columns disappearing at times
const initialColspan = headers1.length - 2

const secondaryHeadersNormal = [
    {
        name: '',
        colspan: initialColspan - +!splitYears,
        headerClass: 'th-name-header',
    },
    {
        name: '<em>ft/s</em>',
        colspan: 1,
        headerClass: `th-secondary-em ${!showSprintSpeed ? 'hide' : ''}`,
    },
    {
        name: '',
        colspan: 2,
        headerClass: 'th-name-header',
    },
    {
        name: '<em>SB + BK</em>',
        colspan: 1,
        headerClass: 'th-secondary-em',
    },
    {
        name: '<em>CS + PK</em>',
        colspan: 1,
        headerClass: 'th-secondary-em',
    },
    {
        name: '<em>Dist (ft)<br/>all SB<br/> opps</em>',
        colspan: 1,
        headerClass: 'th-secondary-em border-left border-right border-top',
    },
    {
        name: '<em>Dist (ft)<br/>SB att<br/>only</em>',
        colspan: 1,
        headerClass: 'th-secondary-em border-right border-top',
    },
]
const secondaryHeadersExpanded = [
    {
        name: '',
        colspan: initialColspan - !+splitYears,
        headerClass: 'th-name-header',
    },
    {
        name: '<em>ft/s</em>',
        colspan: 1,
        headerClass: `th-secondary-em ${!showSprintSpeed ? 'hide' : ''}`,
    },
    {
        name: '',
        colspan: 2,
        headerClass: 'th-name-header',
    },
    {
        name: `<em>Runner on ${prevBaseMap[target_base]}<br/>(Only Runner On)</em>`,
        colspan: headersExpanded1.length,
        headerClass: 'th-secondary-em border-bottom',
    },
    {
        name: '<em>SB + BK</em>',
        colspan: 1,
        headerClass: 'th-secondary-em',
    },
    {
        name: '<em>CS + PK</em>',
        colspan: 1,
        headerClass: 'th-secondary-em',
    },
    {
        name: `<em>Runner Dist from ${prevBaseMap[target_base]}<br/>on all SB Opps (ft)</em>`,
        colspan: 3,
        headerClass: 'th-secondary-em border-right border-left border-top',
    },
    {
        name: `<em>Runner Dist from ${prevBaseMap[target_base]}<br/>on SB Attempts Only (ft)</em>`,
        colspan: 3,
        headerClass: 'th-secondary-em border-right border-top',
    },
]

export const secondaryHeaders = {
    normal: secondaryHeadersNormal,
    expanded: secondaryHeadersExpanded,
}

export const allAttemptsSecondaryHeaders = [
    {
        name: '',
        colspan: 8,
        headerClass: 'th-name-header',
    },
    {
        name: `<em>Runner Distance from ${prevBaseMap[target_base]}<br/>on SB Attempts (ft)</em>`,
        colspan: 3,
        headerClass: 'th-secondary-em border-bottom',
    },
    {
        name: '',
        colspan: 1,
        headerClass: 'th-name-header',
    },
]

export const allAttemptsHeaders = [
    {
        name: 'Date',
        field: 'game_date',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        displayFn: (val) => val.slice(0, 10),
    },
    {
        name: 'Catcher Name',
        field: 'catcher_name',
        headerClass: 'th-component-header align-left th-vertical-bottom',
        fieldClass: 'tr-data align-left',
        displayFn: (catcher_name, d) =>
            `<a href="/savant-player/${d.catcher_id}" target="_blank">
                ${lastNameFirstInitial(catcher_name)}
            </a>`,
    },
    {
        name: 'Pitcher Name',
        field: 'pitcher_name',
        headerClass: 'th-component-header align-left th-vertical-bottom',
        fieldClass: 'tr-data align-left',
        displayFn: (pitcher_name, d) =>
            `<a href="/savant-player/${d.entity_id}" target="_blank">
                ${lastNameFirstInitial(pitcher_name)}
            </a>`,
    },
    {
        name: 'Runner Name',
        field: 'runner_name',
        headerClass: 'th-component-header align-left th-vertical-bottom',
        fieldClass: 'tr-data align-left',
        displayFn: (runner_name, d) =>
            `<a href="/savant-player/${d.runner_id}" target="_blank">
                ${lastNameFirstInitial(runner_name)}
            </a>`,
    },
    {
        name: 'Fielder Name',
        field: 'fielder_name',
        headerClass: 'th-component-header align-left th-vertical-bottom',
        fieldClass: 'tr-data align-left',
        displayFn: (fielder_name, d) =>
            fielder_name
                ? `<a href="/savant-player/${d.fielder_id}" target="_blank">
                ${lastNameFirstInitial(fielder_name)}
            </a>`
                : '--',
    },
    {
        name: 'Target<br/>Base',
        field: 'target_base',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
    },
    {
        name: 'Result',
        field: 'runner_moved_cd',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
    },
    {
        name: 'Runner <br/>Stealing <br/> Runs',
        field: 'runs_stolen_on_running_act',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        displayFn: (val) => guff(val, 'runner_stealing_runs_drawer'),
        type: 'number',
    },
    {
        name: 'Lead<br/>Distance<br/>Gained',
        field: 'r_sec_minus_prim_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left',
        fieldClass: 'tr-data align-center border-left',
        type: 'number',
        displayFn: (val) => guff(val, 'r_sec_minus_prim_lead'),
        toolTip:
            'Lead Distance Gained: Distance in feet a runner has advanced from the start of delivery to pitch release',
    },
    {
        name: "At Pitcher's<br/>First Move",
        field: 'r_primary_lead',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn: (val) => guff(val, 'r_primary_lead'),
    },
    {
        name: 'At Pitch<br/>Release',
        field: 'r_secondary_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-right',
        fieldClass: 'tr-data align-center border-right',
        type: 'number',
        displayFn: (val) => guff(val, 'r_secondary_lead'),
    },
    {
        name: '',
        field: 'entity_id',
        headerClass:
            'th-component-header align-center th-vertical-bottom video-col cursor-initial',
        fieldClass: 'tr-data video-col',
        type: 'number',
        sortable: false,
        displayFn: (value, { play_id }) => `
        <a href="/sporty-videos?playId=${play_id}" target="_BLANK">
            <img src="/site-core/images/video.png" style="margin-right: 4px; width: 25px; height: 13px; cursor: pointer;">
        </a>
        `,
    },
]

export const groupedHeaders = [
    {
        name: 'Player',
        field: 'entity_name',
        headerClass: 'th-component-header align-left th-vertical-bottom',
        fieldClass: 'tr-data align-left',
        displayFn: (name, d) =>
            `<a href="/savant-player/${d.entity_id}" target="_blank">
                ${lastNameFirstInitial(name)}
            </a>`,
    },
    {
        name: 'Runner <br />Stealing <br/> Runs',
        field: 'runs_stolen_on_running_act',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-top border-left border-right',
        fieldClass: 'tr-data border-left border-right',
        type: 'number',
        displayFn: (val) => guff(val, 'runs_stolen_on_running_act_drawer'),
    },
    {
        name: 'SB Attempts',
        field: 'sba',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data border-right',
        type: 'number',
    },
    {
        name: 'SB',
        field: 'sb',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data',
        type: 'number',
    },
    {
        name: 'CS',
        field: 'cs',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data',
        type: 'number',
    },
    {
        name: 'PK',
        field: 'pk',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data',
        type: 'number',
        toolTip: 'Pickoffs',
    },
    {
        name: 'BK',
        field: 'bk',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data',
        type: 'number',
        toolTip: 'Balks',
    },
    {
        name: 'FB',
        field: 'fb',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data',
        type: 'number',
        toolTip:
            'Forced Balks: Balks caused by a pitcher failing to throw a runner out on a third disengagement',
    },
    {
        name: 'Lead<br/>Distance<br/>Gained',
        field: 'r_sec_minus_prim_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-left',
        fieldClass: 'tr-data align-center border-left',
        type: 'number',
        displayFn: (val) => guff(val, 'r_sec_minus_prim_lead'),
        toolTip:
            'Lead Distance Gained: Distance a runner has advanced from the start of delivery to pitch release (in feet)',
    },
    {
        name: "At<br/> Pitcher's<br/>First Move",
        field: 'r_primary_lead',
        headerClass: 'th-component-header align-center th-vertical-bottom',
        fieldClass: 'tr-data align-center',
        type: 'number',
        displayFn: (val) => guff(val, 'r_primary_lead'),
    },
    {
        name: 'At<br/> Pitch<br/>Release',
        field: 'r_secondary_lead',
        headerClass:
            'th-component-header align-center th-vertical-bottom border-right',
        fieldClass: 'tr-data align-center border-right',
        type: 'number',
        displayFn: (val) => guff(val, 'r_secondary_lead'),
        toolTip:
            'Distance the runner is from first at the pitch release (in feet)',
    },
]

export const groupedSecondaryHeaders = [
    {
        name: '',
        colspan: 8,
        headerClass: 'th-name-header',
    },
    {
        name: `<em>Avg Runner Distance from ${prevBaseMap[target_base]}<br/>on SB Attempts (ft)</em>`,
        colspan: 3,
        headerClass: 'th-secondary-em border-bottom',
    },
]
